import { ChangeDetectionStrategy, Component, ElementRef, Input, NgZone, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';

import { AgmGeocoder, GeocoderResult, MapsAPILoader } from '@agm/core';
import { google } from 'google-maps';
import { latLng, MapOptions, tileLayer, marker } from 'leaflet';
import { AddressModel } from '@ov-suite/models-shared';
import { AbstractValueAccessor, MakeProvider } from '../input/abstruct-value-accessor';

@Component({
  selector: 'ov-suite-agm-map',
  templateUrl: './agm-map.component.html',
  styleUrls: ['./agm-map.component.scss'],
  providers: [MakeProvider(AgmMapComponent)],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class AgmMapComponent extends AbstractValueAccessor<AddressModel> implements OnInit {
  @Input() id: string;

  @Input() danger: boolean;

  @Input() disabled: boolean;

  @Input() title = 'Enter Address';

  @ViewChild('mapSearch')
  public searchElementRef: ElementRef;

  lines: string[] = [];

  postalCode = '';

  latitude = -29;

  longitude = 24;

  zoom = 5;

  center = latLng(this.latitude, this.longitude);

  pin = marker([this.latitude, this.longitude], { draggable: true });

  address: string;

  private geoCoder: AgmGeocoder;

  private searchTimeout = null;

  options: MapOptions = {
    layers: [tileLayer('http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18, attribution: '...' })],
    doubleClickZoom: false,
  };

  constructor(private readonly mapsAPILoader: MapsAPILoader, private readonly ngZone: NgZone, private readonly cdRef: ChangeDetectorRef) {
    super();
  }

  writeValue(value: AddressModel) {
    if (value) {
      if (typeof value.geography === 'string') {
        [this.longitude, this.latitude] = JSON.parse(value.geography).coordinates;
      } else if (value.geography) {
        [this.longitude, this.latitude] = value.geography.coordinates;
      }

      this.address = [value.line1, value.line2, value.line3, value.line4, value.line5, value.line6, value.postalCode]
        .filter(c => !!c)
        .join(', ');

      this.center = latLng(this.latitude, this.longitude);
      if (!this.latitude) {
        this.pin = null;
      } else {
        this.pin = marker(this.center, { draggable: true })
          .bindTooltip(this.address)
          .on('dragend', event => {
            this.markerDragEnd({ coords: event.target['_latlng'] });
          });
      }
      this.zoom = 12;
    }
    this.val = value;
    this.onChange(value);
  }

  valueChange(item: AddressModel) {
    this.writeValue(item);
  }

  ngOnInit() {
    this.initialiseMap();
  }

  initialiseMap() {
    this.mapsAPILoader.load().then(() => {
      this.geoCoder = new AgmGeocoder(this.mapsAPILoader);

      const autocomplete = new google.maps.places.Autocomplete(this.searchElementRef.nativeElement, {
        componentRestrictions: {
          country: ['za', 'zw', 'ls', 'sz', 'mz', 'bw', 'na'],
        },
      });
      autocomplete.addListener('place_changed', () => {
        if (autocomplete.getPlace()) {
          this.ngZone.run(() => {
            // get the place result
            const place: google.maps.places.PlaceResult = autocomplete.getPlace();

            // verify result
            if (place.geometry === undefined || place.geometry === null) {
              return;
            }
            this.lines = [];
            const [type] = place.types;
            if (place.name) {
              this.lines.push(place.name);
            }
            place.address_components.forEach((c, index) => {
              if (c.types.some(t => t === 'postal_code')) {
                this.postalCode = c.long_name;
                return;
              }
              /**
               *  place.name appears to always be the first 2 lines of place.address_components when type is 'street_address',
               *  so we skip them here.
               */
              if (type === 'street_address' && index < 2) {
                return;
              }
              this.lines.push(c.long_name);
            });

            // set latitude, longitude and zoom
            this.address = place.formatted_address;
            this.latitude = place.geometry.location.lat();
            this.longitude = place.geometry.location.lng();
            this.zoom = 18;
            this.emitCoOrdinates();
          });
        }
      });
    });
  }

  locationChange() {
    this.searchTimeout = null;
    this.searchTimeout = setTimeout(() => {
      this.getAddress(this.latitude, this.longitude);
    }, 500);
  }

  emitCoOrdinates() {
    const map = new AddressModel();
    map.id = this.val?.id;
    map.geography ??= { type: 'Point' };
    map.geography.coordinates = [this.longitude, this.latitude];
    this.lines.forEach((line, index) => {
      if (index < 6) {
        map[`line${index + 1}`] = line;
      }
    });
    map.postalCode = this.postalCode;
    this.valueChange(map);
  }

  markerDragEnd(event) {
    this.latitude = event.coords.lat;
    this.longitude = event.coords.lng;
    this.getAddress(this.latitude, this.longitude);
  }

  getAddress(latitude, longitude) {
    this.geoCoder.geocode({ location: { lat: latitude, lng: longitude } }).subscribe((results: GeocoderResult[]) => {
      if (results && results.length > 0) {
        this.zoom = 12;
        this.address = results[0].formatted_address;
      } else {
        this.address = '';
      }
      this.emitCoOrdinates();
      this.cdRef.detectChanges();
    });
  }
}
